
import React, { useEffect, useContext, createContext, useState, useCallback } from "react";
import { UndoToast } from "./UndoToast";
import { useToast } from "@/hooks/use-toast";

interface UndoToastProviderProps {
  children: React.ReactNode;
}

interface UndoToastContextType {
  showUndoToast: (title: string, description: string, onUndo: () => void) => void;
}

const UndoToastContext = createContext<UndoToastContextType | undefined>(undefined);

export const UndoToastProvider: React.FC<UndoToastProviderProps> = ({ children }) => {
  const { toast } = useToast();
  const [activeToasts, setActiveToasts] = useState<{id: string, onUndo: () => void}[]>([]);
  
  // Set indefinite duration for undo toasts
  const DEFAULT_DURATION = undefined;

  const showUndoToast = useCallback((title: string, description: string, onUndo: () => void) => {
    try {
      const { id } = toast({
        title,
        description,
        action: (
          <UndoToast
            title={title}
            description={description}
            duration={undefined}
            onUndo={() => {
              onUndo();
              // Remove this toast from active toasts
              setActiveToasts(prev => prev.filter(t => t.id !== id));
            }}
          />
        ),
        duration: DEFAULT_DURATION,
      });
      
      // Store the toast ID and its undo handler
      setActiveToasts(prev => [...prev, { id, onUndo }]);
    } catch (error) {
      console.error("Error showing undo toast:", error);
      // Fallback to a standard toast if there's an error
      toast({
        title,
        description,
        variant: "default",
        duration: DEFAULT_DURATION,
      });
    }
  }, [toast]);

  // Clean up any active toasts when component unmounts
  useEffect(() => {
    return () => {
      // No cleanup needed for toasts as they're managed by the toast system
    };
  }, []);

  return (
    <UndoToastContext.Provider value={{ showUndoToast }}>
      {children}
    </UndoToastContext.Provider>
  );
};

export const useUndoToast = (): UndoToastContextType => {
  const context = useContext(UndoToastContext);
  if (context === undefined) {
    throw new Error("useUndoToast must be used within an UndoToastProvider");
  }
  return context;
};
